import * as React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { HEADER_HEIGHT } from '@/theme';

interface StickyHeaderBarProps {
  /** Pin offset from the viewport top; defaults to sitting flush under the app header. */
  top?: number;
  /**
   * Horizontal page padding (theme spacing units) the bar widens into once it is stuck, so a
   * pinned header reads as a full-width toolbar rather than a card stopped mid-scroll. Left at 0
   * for a bar that lives inside a card, where widening past its parent only gets it clipped.
   */
  bleed?: number;
  zIndex?: number;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Pins its children below the app header while the page scrolls, and — given a `bleed` — widens
 * them into the page's horizontal padding once pinned.
 *
 * Once stuck it also squares the corners: a bar spanning the page edge to edge reads as a toolbar,
 * and rounded corners on a toolbar look like a card that stopped mid-scroll. Nothing else changes
 * (the shadow a caller sets stays as it is), so it still reads as the same card that pinned.
 */
export default function StickyHeaderBar({
  top = HEADER_HEIGHT,
  bleed = 0,
  zIndex = 1000,
  children,
  sx,
}: StickyHeaderBarProps) {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = React.useState(false);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    // No observer (jsdom): the bar still pins, it just never widens.
    if (!sentinel || !bleed || typeof IntersectionObserver === 'undefined') return undefined;
    // A zero-height sentinel right above the bar: once it scrolls past the pin line the bar is
    // stuck. IntersectionObserver reports the crossing without any scroll-handler math.
    const observer = new IntersectionObserver(([entry]) => setStuck(entry ? !entry.isIntersecting : false), {
      rootMargin: `-${top + 1}px 0px 0px 0px`,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [top, bleed]);

  return (
    <>
      <Box ref={sentinelRef} aria-hidden />
      <Box
        data-stuck={stuck ? '' : undefined}
        sx={[
          {
            position: 'sticky',
            top,
            zIndex,
            transition: (theme) =>
              theme.transitions.create(['margin', 'border-radius'], { duration: theme.transitions.duration.shorter }),
          },
          ...(Array.isArray(sx) ? sx : [sx]),
          // After the caller's sx so the pinned width and square corners always win while it is stuck.
          stuck && { mx: -bleed, borderRadius: 0 },
        ]}
      >
        {children}
      </Box>
    </>
  );
}
